/**
 * dev-variant-autoadjust.js
 *
 * Auto-adjusts sibling variant options for non-rectangular matrices (products that don't
 * offer every option combination, e.g. a Colour only made in certain Sizes).
 *
 * A capture-phase `change` listener runs before the variant-picker's own handler. Options
 * are a left-to-right priority — the option the shopper just changed is honoured, EARLIER
 * options are anchors that are never rewritten (so picking a Size never changes the chosen
 * Colour), and only LATER options are auto-fit to the first (preferably available) matching
 * variant.
 *
 * Note: hiding option values that aren't available for the current selection is done
 * SERVER-SIDE in snippets/variant-main-picker.liquid (gated by the section's
 * hide_unavailable_options setting) so the morphed markup is already correct and there's no
 * flash. The hidden radios stay in the DOM, so this auto-adjust can still switch to them.
 *
 * Data comes from a JSON island: <script type="application/json" class="dev-pdp-variant-matrix">
 * containing [{ o: [optionValue, ...], a: available }].
 */
(function () {
  function selectedValues(fieldsets) {
    return fieldsets.map(function (fs) {
      var checked = fs.querySelector('input:checked');
      if (checked) return checked.value;
      var select = fs.querySelector('select');
      return select ? select.value : null;
    });
  }

  function wire(matrixEl) {
    var section = matrixEl.closest('.shopify-section');
    if (!section || section.__pdpAutoAdjustWired) return;

    var variants;
    try {
      variants = JSON.parse(matrixEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;
    section.__pdpAutoAdjustWired = true;

    section.addEventListener(
      'change',
      function (e) {
        var input = e.target;
        if (!input || !input.closest) return;
        var picker = input.closest('variant-picker');
        if (!picker) return;

        // An option with >8 values renders as a <select> inside a plain
        // <div class="variant-option--dropdowns"> (see variant-main-picker.liquid),
        // not a <fieldset> — include both so a product with any dropdown option
        // (e.g. a 17-value Size) isn't silently skipped below the length-2 guard.
        var fieldsets = [].slice.call(picker.querySelectorAll('fieldset, .variant-option--dropdowns'));
        if (fieldsets.length < 2) return;

        var changedFieldset = input.closest('fieldset, .variant-option--dropdowns');
        var changedIndex = fieldsets.indexOf(changedFieldset);
        if (changedIndex < 0) return;

        // Current selection per option (index-aligned with variant.options).
        var selected = selectedValues(fieldsets);

        var comboExists = variants.some(function (v) {
          return v.o.every(function (val, i) {
            return val === selected[i];
          });
        });
        if (comboExists) return; // valid combo — nothing to fix

        // First (preferably available) variant that has the chosen value AND keeps every
        // EARLIER option unchanged. Options are a left-to-right priority: the option just
        // changed is honoured, options BEFORE it are anchors that must be preserved (so
        // picking a Size never rewrites the chosen Colour), and only options AFTER it are
        // adjusted. Selecting a Size the current Colour isn't made in therefore leaves the
        // Colour alone instead of flipping it to a different colour.
        var candidates = variants.filter(function (v) {
          if (v.o[changedIndex] !== input.value) return false;
          for (var j = 0; j < changedIndex; j++) {
            if (v.o[j] !== selected[j]) return false;
          }
          return true;
        });
        if (!candidates.length) return;
        var pick =
          candidates.filter(function (v) {
            return v.a;
          })[0] || candidates[0];

        // Point only the LATER options at that variant's value; leave the changed option
        // and every earlier (anchor) option untouched.
        fieldsets.forEach(function (fs, i) {
          if (i <= changedIndex) return;
          var want = pick.o[i];

          var radio = [].slice.call(fs.querySelectorAll('input[type="radio"]')).filter(function (r) {
            return r.value === want;
          })[0];

          if (radio) {
            if (!radio.checked) radio.checked = true;
            return;
          }

          var select = fs.querySelector('select');
          if (select) {
            select.querySelectorAll('option[selected]').forEach(function (o) {
              o.removeAttribute('selected');
            });
            var option = [].slice.call(select.options).filter(function (o) {
              return o.value === want;
            })[0];
            if (option) {
              option.setAttribute('selected', 'selected');
              option.selected = true;
            }
          }
        });
      },
      true // capture phase — before the variant-picker's own change handler
    );
  }

  function boot() {
    document.querySelectorAll('.dev-pdp-variant-matrix').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
